import React from 'react';
import { Box, Card, Typography, Chip, IconButton, Divider, Tooltip } from '@material-ui/core';
import FlagIcon from '@material-ui/icons/Flag';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import InputIcon from '@material-ui/icons/Input';
import LinkIcon from '@material-ui/icons/Link';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { TEXT, tint } from '../../shared_components';

const HEX_PATTERN = /^#([0-9a-f]{6})$/i;

/* Legacy preset ids from before the free-form colour picker, kept only to render stages saved under the old contract. */
const LEGACY_PRESET_HEX = { blue: '#1677FF', green: '#12B76A', orange: '#FF7900', purple: '#673DE6', red: '#f85149', grey: '#8b949e' };

const resolveColourHex = (colourId) => (HEX_PATTERN.test(colourId) ? colourId : LEGACY_PRESET_HEX[colourId] || LEGACY_PRESET_HEX.blue);

const formatDate = (date) => (date ? new Date(date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-');

const ACTIONS = [
  { key: 'edit', Icon: EditIcon, color: '#344054', border: '#DCE1EA' },
  { key: 'delete', Icon: DeleteOutlineIcon, color: '#F04444', border: '#FFD2D2' },
  { key: 'more', Icon: MoreVertIcon, color: '#667085', border: '#DCE1EA' },
];

const StageCard = ({
  stage, onEdit, onDelete, onMore, isLast, draggable, isDragging, isDragOver, onDragStart, onDragEnter, onDragOver, onDrop, onDragEnd,
}) => {
  const colourHex = resolveColourHex(stage?.colour);
  const lightHex = tint(colourHex);
  const handlers = { edit: onEdit, delete: onDelete, more: onMore };

  const metaItems = [
    { icon: <PersonOutlineIcon />, label: 'Created by', value: stage?.created_by?.name, flex: 1.4 },
    { icon: <CalendarTodayIcon />, label: 'Created at', value: formatDate(stage?.created_at), flex: 1.3 },
    { icon: <InputIcon />, label: 'Entry access', value: stage?.entry_access?.label, flex: 1.2 },
    { icon: <ExitToAppIcon />, label: 'Exit access', value: stage?.exit_access?.label, flex: 1.1 },
    { icon: <LinkIcon />, label: 'Linked plans', value: stage?.linked_plan_count || 0, flex: 0.8 },
    { icon: <VisibilityOffIcon />, label: 'Inactivity', value: stage?.suppress_inactivity ? 'Suppressed' : 'Not suppressed', flex: 1.1 },
  ];

  return (
    <Box
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        display: 'flex', alignItems: 'stretch', width: '100%', position: 'relative', marginBottom: 16,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      <Box style={{ width: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flexShrink: 0 }}>
        {!isLast && <Box style={{ position: 'absolute', top: 'calc(50% + 26px)', height: 'calc(50% + 26px + 16px)', width: 3, backgroundColor: lightHex, borderRadius: 4 }} />}
        <Box style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: colourHex, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, zIndex: 1, boxShadow: `0 0 0 6px ${lightHex}, 0 2px 6px ${lightHex}` }}>
          {stage?.order}
        </Box>
      </Box>

      <Card
        elevation={0}
        style={{
          flex: 1, borderRadius: 14, border: isDragOver ? '1px dashed #7E43A9' : '1px solid #E1DCE8', borderLeft: `4px solid ${colourHex}`, overflow: 'hidden', background: '#FFFFFF', transition: 'box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(23, 32, 51, 0.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        <Box style={{ display: 'flex', alignItems: 'center', padding: '8px 20px', minHeight: 64 }}>
          {draggable ? (
            <Box style={{ cursor: 'grab', display: 'flex', alignItems: 'center', marginRight: 8, color: '#98A2B3' }}>
              <DragIndicatorIcon style={{ fontSize: 20 }} />
            </Box>
          ) : null}
          <Box style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: lightHex, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 12 }}>
            <FlagIcon style={{ fontSize: 20, color: colourHex }} />
          </Box>

          <Box style={{ flex: 1, minWidth: 0 }}>
            <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
              <Tooltip title={stage?.name || ''}>
                <Typography noWrap style={{ fontSize: 15, fontWeight: 600, color: TEXT.primary, maxWidth: 400 }}>{stage?.name}</Typography>
              </Tooltip>
              {stage?.stage_type?.label ? (
                <Chip size="small" label={stage.stage_type.label} style={{ background: '#EAF2FF', color: '#2675E8', fontWeight: 500, fontSize: 11, height: 24 }} />
              ) : null}
            </Box>

            <Typography style={{ color: stage?.description ? TEXT.secondary : '#A0A8B7', fontSize: 12.5, fontStyle: stage?.description ? 'normal' : 'italic', lineHeight: 1.5, maxWidth: 850 }}>
              {stage?.description || ''}
            </Typography>
          </Box>

          <Chip
            label={stage?.is_active ? 'Active' : 'Inactive'}
            size="small"
            icon={<Box style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: stage?.is_active ? '#28A86B' : '#9AA2B1', marginLeft: 8 }} />}
            style={{ marginRight: 18, background: stage?.is_active ? '#E9F8F1' : '#F1F2F4', color: stage?.is_active ? '#168453' : '#687080', fontWeight: 500, fontSize: 12, height: 26 }}
          />

          <Divider orientation="vertical" flexItem style={{ marginRight: 12 }} />

          {ACTIONS.map(({ key, Icon, color, border }) => (handlers[key] ? (
            <IconButton key={key} size="small" onClick={() => handlers[key](stage)} style={{ border: `1px solid ${border}`, borderRadius: 9, marginRight: key !== 'more' ? 8 : 0 }}>
              <Icon style={{ fontSize: key === 'edit' ? 17 : 18, color }} />
            </IconButton>
          ) : null))}
        </Box>

        <Divider />

        <Box style={{ display: 'flex', alignItems: 'stretch', padding: '8px 20px', backgroundColor: lightHex }}>
          {metaItems.map((item, index) => (
            <React.Fragment key={item.label}>
              {index > 0 && <Divider orientation="vertical" flexItem style={{ backgroundColor: '#E7E3EB', margin: '4px 0' }} />}
              <MetaItem {...item} colourHex={colourHex} />
            </React.Fragment>
          ))}
        </Box>
      </Card>
    </Box>
  );
};

const MetaItem = ({ icon, label, value, secondary, flex = 1, colourHex = '#7E43A9' }) => (
  <Box style={{ display: 'flex', alignItems: 'center', flex, minWidth: 0, padding: '0 10px' }}>
    <Box style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 8, flexShrink: 0 }}>
      {React.cloneElement(icon, { style: { fontSize: 14, color: colourHex } })}
    </Box>
    <Box style={{ minWidth: 0 }}>
      <Typography style={{ color: '#7A8497', fontSize: 11, display: 'block', marginBottom: 2 }}>{label}</Typography>
      <Tooltip title={value || '-'}>
        <Typography noWrap style={{ color: TEXT.primary, fontSize: 12, fontWeight: 500 }}>{value || '-'}</Typography>
      </Tooltip>
      {secondary ? (
        <Tooltip title={secondary}>
          <Typography noWrap style={{ color: '#98A2B3', fontSize: 10, display: 'block', marginTop: 1 }}>{secondary}</Typography>
        </Tooltip>
      ) : null}
    </Box>
  </Box>
);

export default StageCard;
